'use client';

import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@r/components/ui/button.tsx';
import { Calendar } from '@r/components/ui/calendar.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';
import { fromDateInputValue } from '@r/utils/date-utils.ts';
import { cn } from '@r/utils/utils.ts';

export interface DatePickerProps {
  value?: Date | string | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
  isDateDisabled?: (date: Date) => boolean;
  placeholder?: string;
  className?: string;
  /** Format `date-fns` du libellé de la date sélectionnée. Défaut `PPP` (date longue). */
  formatStr?: string;
}

/** Sélecteur de date unique réutilisable dans les formulaires. */
export function DatePicker({
  value,
  onChange,
  disabled = false,
  isDateDisabled,
  placeholder,
  className,
  formatStr = 'PPP'
}: DatePickerProps) {
  const { t } = useTranslation();
  const selectedDate = fromDateInputValue(value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full pl-3 text-left font-normal',
            !selectedDate && 'text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        >
          {selectedDate ? (
            <span className="truncate">{format(selectedDate, formatStr, { locale: fr })}</span>
          ) : (
            <span className="truncate">{placeholder ?? t('form.shop.date.pick')}</span>
          )}
          <CalendarIcon className="ml-auto size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            if (!disabled) {
              onChange(date ?? null);
            }
          }}
          disabled={(date) => {
            if (disabled) return true;
            return isDateDisabled?.(date) ?? false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
